import {
  Users, Home, Phone, Video, ClipboardCheck, Building2, Coffee, Car, CalendarDays,
  ListChecks, FileText, Wallet, FileClock,
} from 'lucide-react';

// Google Calendar's event colours (the names are the ones Google uses, so the server keeps the
// same list in utils/scCalendarRules.js). `ink` is the text colour that reads on the fill.
export const COLORS = {
  tomato: { hex: '#d50000', ink: '#ffffff', label: 'Tomato' },
  flamingo: { hex: '#e67c73', ink: '#ffffff', label: 'Flamingo' },
  tangerine: { hex: '#f4511e', ink: '#ffffff', label: 'Tangerine' },
  banana: { hex: '#f6bf26', ink: '#3b2f00', label: 'Banana' },
  sage: { hex: '#33b679', ink: '#ffffff', label: 'Sage' },
  basil: { hex: '#0b8043', ink: '#ffffff', label: 'Basil' },
  peacock: { hex: '#039be5', ink: '#ffffff', label: 'Peacock' },
  blueberry: { hex: '#3f51b5', ink: '#ffffff', label: 'Blueberry' },
  lavender: { hex: '#7986cb', ink: '#ffffff', label: 'Lavender' },
  grape: { hex: '#8e24aa', ink: '#ffffff', label: 'Grape' },
  graphite: { hex: '#616161', ink: '#ffffff', label: 'Graphite' },
};
export const COLOR_KEYS = Object.keys(COLORS);
// Coordinators get a colour from their id, so it stays the same on every screen and device.
const AUTO = ['peacock', 'grape', 'sage', 'tangerine', 'blueberry', 'flamingo', 'basil', 'lavender', 'banana', 'tomato'];
export function autoColor(id) {
  if (!id) return 'graphite';
  let h = 0;
  for (const ch of String(id)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AUTO[h % AUTO.length];
}
// A colour key → { hex, ink }, with a light tint for past or cancelled appointments.
export const colorOf = (key) => COLORS[key] || COLORS.graphite;
export const tint = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// What an appointment is. The list matches the server's KINDS.
export const KINDS = [
  { value: 'meeting', label: 'Participant meeting', short: 'Meeting', icon: Users },
  { value: 'home_visit', label: 'Home visit', short: 'Home visit', icon: Home },
  { value: 'phone', label: 'Phone call', short: 'Call', icon: Phone },
  { value: 'video', label: 'Video call', short: 'Video', icon: Video },
  { value: 'plan_review', label: 'Plan review meeting', short: 'Plan review', icon: ClipboardCheck },
  { value: 'provider_meeting', label: 'Provider meeting', short: 'Provider', icon: Building2 },
  { value: 'internal', label: 'Team or internal', short: 'Internal', icon: Coffee },
  { value: 'travel', label: 'Travel', short: 'Travel', icon: Car },
  { value: 'other', label: 'Other', short: 'Other', icon: CalendarDays },
];
export const kindOf = (v) => KINDS.find((k) => k.value === v) || KINDS[0];

// Matches the server's REMINDERS (minutes before the start). '' = no reminder.
export const REMINDERS = [
  { value: '', label: 'No reminder' },
  { value: '10', label: '10 minutes before' },
  { value: '15', label: '15 minutes before' },
  { value: '30', label: '30 minutes before' },
  { value: '60', label: '1 hour before' },
  { value: '120', label: '2 hours before' },
  { value: '1440', label: '1 day before' },
];
export const reminderText = (m) => (REMINDERS.find((r) => r.value === String(m ?? '')) || REMINDERS[0]).label;

export const REPEATS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Every weekday (Monday to Friday)' },
  { value: 'weekly', label: 'Every week' },
  { value: 'fortnightly', label: 'Every fortnight' },
  { value: 'monthly', label: 'Every month' },
];

// The caseload's key dates (read-only here; they are set on the engagement).
export const KEY_TYPES = {
  task: { label: 'Tasks', one: 'Task', color: 'peacock', icon: ListChecks },
  plan_review: { label: 'Plan reviews', one: 'Plan review', color: 'grape', icon: ClipboardCheck },
  progress_report: { label: 'Progress reports', one: 'Progress report', color: 'tangerine', icon: FileClock },
  report: { label: 'Reports due', one: 'Report due', color: 'tomato', icon: FileText },
  funding_release: { label: 'Funding releases', one: 'Funding release', color: 'basil', icon: Wallet },
};
export const KEY_TYPE_KEYS = Object.keys(KEY_TYPES);
